import { useEffect, useState } from "react";
import { Loader2, CheckCircle2, Power, PowerOff, Plus, Archive } from "lucide-react";
import { supabase } from "@/lib/supabaseClient";
import type { QuestionSet, Question } from "@/types/database";

export default function AdminQuestionsPage() {
  const [sets, setSets] = useState<QuestionSet[]>([]);
  const [activeSetId, setActiveSetId] = useState<string | null>(null);
  const [questions, setQuestions] = useState<Question[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  async function loadSets() {
    setLoading(true);
    const { data, error } = await supabase
      .from("question_sets")
      .select("*")
      .order("created_at", { ascending: false });
    if (error) setError(error.message);
    setSets((data ?? []) as QuestionSet[]);
    const active = (data ?? []).find((s) => s.active) ?? data?.[0];
    if (active) setActiveSetId(active.id);
    setLoading(false);
  }

  async function loadQuestions(setId: string) {
    const { data, error } = await supabase
      .from("questions")
      .select("*")
      .eq("question_set_id", setId)
      .order("position", { ascending: true })
      .limit(200);
    if (error) setError(error.message);
    setQuestions((data ?? []) as Question[]);
  }

  useEffect(() => { void loadSets(); }, []);
  useEffect(() => { if (activeSetId) void loadQuestions(activeSetId); }, [activeSetId]);

  async function setActive(setId: string) {
    setBusyId(setId);
    // Deactivate any others, activate this one
    await supabase.from("question_sets").update({ active: false }).neq("id", setId);
    const { error } = await supabase.from("question_sets").update({ active: true, updated_at: new Date().toISOString() }).eq("id", setId);
    if (error) setError(error.message);
    setBusyId(null);
    await loadSets();
  }

  async function archiveSet(setId: string) {
    setBusyId(setId);
    await supabase.from("question_sets").update({ archived: true, active: false }).eq("id", setId);
    setBusyId(null);
    await loadSets();
  }

  async function toggleQuestion(q: Question) {
    setBusyId(q.id);
    const { error } = await supabase.from("questions").update({ active: !q.active }).eq("id", q.id);
    if (error) setError(error.message);
    setBusyId(null);
    if (activeSetId) await loadQuestions(activeSetId);
  }

  return (
    <div className="space-y-6">
      <header>
        <h1 className="font-display text-3xl text-white">Question banks</h1>
        <p className="text-steel-300">Activate one set at a time. Students play whichever is active.</p>
      </header>

      {error && <div className="pill-bad px-3 py-2">{error}</div>}

      {loading ? (
        <div className="text-steel-300 flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div>
      ) : (
        <section className="card-dark p-4">
          <h2 className="font-display text-lg text-white mb-3">Sets</h2>
          <table className="w-full text-sm">
            <thead className="text-steel-300 text-xs uppercase tracking-wider">
              <tr>
                <th className="text-left p-2">Title</th>
                <th className="text-left p-2">Status</th>
                <th className="text-right p-2">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-steel-700">
              {sets.map((s) => (
                <tr key={s.id} className={s.id === activeSetId ? "bg-steel-800/40" : ""}>
                  <td className="p-2">
                    <button onClick={() => setActiveSetId(s.id)} className="text-left">
                      <div className="font-medium">{s.title}</div>
                      <div className="text-xs text-steel-400">{s.description ?? ""}</div>
                    </button>
                  </td>
                  <td className="p-2">
                    {s.archived ? <span className="pill-bad">Archived</span>
                      : s.active ? <span className="pill-ok">Active</span>
                      : <span className="pill-warn">Draft</span>}
                  </td>
                  <td className="p-2 text-right">
                    {!s.archived && !s.active && (
                      <button onClick={() => setActive(s.id)} className="btn-outline mr-2" disabled={busyId === s.id}>
                        <CheckCircle2 className="w-4 h-4" /> Activate
                      </button>
                    )}
                    {!s.archived && (
                      <button onClick={() => archiveSet(s.id)} className="btn-outline" disabled={busyId === s.id}>
                        <Archive className="w-4 h-4" /> Archive
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>
      )}

      {activeSetId && (
        <section className="card-dark p-4">
          <h2 className="font-display text-lg text-white mb-3">Questions in selected set</h2>
          <p className="text-xs text-steel-400 mb-3">
            Tip: questions imported from .docx default to inactive. Review the answer, then activate.
          </p>
          <ul className="divide-y divide-steel-700">
            {questions.map((q) => (
              <li key={q.id} className="py-3 flex items-start justify-between gap-4">
                <div>
                  <div className="text-sm text-steel-300">Q{q.position} · {q.category ?? "—"} · correct: {q.correct_option}</div>
                  <div className="font-medium">{q.question_text}</div>
                </div>
                <button
                  onClick={() => toggleQuestion(q)}
                  className={"btn-outline " + (q.active ? "" : "border-amber-500 text-amber-300")}
                  disabled={busyId === q.id}
                >
                  {q.active ? <Power className="w-4 h-4" /> : <PowerOff className="w-4 h-4" />}
                  {q.active ? "Active" : "Inactive"}
                </button>
              </li>
            ))}
            {questions.length === 0 && <li className="py-3 text-steel-400">No questions in this set.</li>}
          </ul>
          <div className="mt-3">
            <a href="/admin/upload" className="btn-primary inline-flex">
              <Plus className="w-4 h-4" /> Add via DOCX upload
            </a>
          </div>
        </section>
      )}
    </div>
  );
}
